import React from 'react';
import styled from 'styled-components';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';

const HeroSection = styled.section`
  position: relative;
  padding: 140px 20px 120px;
  background: radial-gradient(circle at top left, rgba(12, 77, 162, 0.12), transparent 55%),
    linear-gradient(135deg, rgba(247, 248, 252, 1) 0%, rgba(235, 239, 252, 1) 100%);

  @media (max-width: 768px) {
    padding: 110px 18px 90px;
  }

  @media (max-width: 480px) {
    padding: 96px 16px 80px;
  }
`;

const HeroInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 42px;
  align-items: center;

  @media (max-width: 768px) {
    gap: 32px;
  }
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
    gap: 18px;
  }
`;

const PageTag = styled.span`
  width: fit-content;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(12, 77, 162, 0.16);
  color: var(--primary);
  letter-spacing: 0.32em;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2.5rem, 4vw, 3.6rem);
  color: var(--secondary);
  line-height: 1.1;
`;

const Intro = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 1.06rem;
  line-height: 1.8;
`;

const ContactPanel = styled.div`
  padding: 32px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  display: grid;
  gap: 20px;

  @media (max-width: 600px) {
    padding: 26px 24px;
    gap: 16px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;

  @media (max-width: 480px) {
    gap: 14px;
  }
`;

const ContactCard = styled.div`
  padding: 20px;
  border-radius: var(--radius-md);
  background: rgba(12, 77, 162, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 480px) {
    padding: 18px 16px;
  }
`;

const ContactLabel = styled.span`
  font-size: 0.82rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--primary);
  font-weight: 600;
`;

const ContactValue = styled.a`
  color: var(--secondary);
  font-size: 1.05rem;
  font-weight: 600;
  text-decoration: none;
  word-break: break-word;

  &:hover {
    color: var(--primary);
  }
`;

const Section = styled.section`
  padding: 110px 20px;
  background: ${({ $alt }) => ($alt ? '#f7f8fc' : '#ffffff')};

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 74px 16px;
  }
`;

const SectionInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  gap: 36px;

  @media (max-width: 768px) {
    gap: 28px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.8rem);
  color: var(--secondary);
`;

const Paragraph = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.75;
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const OfficeCard = styled.article`
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 600px) {
    padding: 24px 22px;
  }
`;

const OfficeTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: var(--secondary);
`;

const OfficeText = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.7;
  font-size: 0.96rem;
`;

const FormCard = styled.div`
  padding: 34px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  display: grid;
  gap: 20px;

  @media (max-width: 600px) {
    padding: 28px 24px;
    gap: 18px;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 18px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;

  @media (max-width: 480px) {
    gap: 14px;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.92rem;
  color: var(--secondary);
  font-weight: 600;
`;

const Input = styled.input`
  padding: 14px 16px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.96);
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(12, 77, 162, 0.15);
  }
`;

const Textarea = styled.textarea`
  padding: 16px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.96);
  font-size: 0.95rem;
  min-height: 160px;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(12, 77, 162, 0.15);
  }
`;

const SubmitButton = styled.button`
  justify-self: flex-start;
  padding: 14px 32px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--primary) 0%, #1c6be1 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: var(--shadow-soft);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-muted);
`;

const ContactPage = () => {
  const { t } = useLanguage();
  const locations = t('pages.contact.locations');

  return (
    <>
      <HeroSection>
        <HeroInner>
          <HeadingGroup>
            <PageTag>{t('pages.contact.tag')}</PageTag>
            <Title>{t('pages.contact.title')}</Title>
            <Intro>{t('pages.contact.intro')}</Intro>
          </HeadingGroup>
          <ContactPanel>
            <SectionTitle>{t('pages.contact.directTitle')}</SectionTitle>
            <ContactGrid>
              <ContactCard>
                <ContactLabel>{t('pages.contact.directLabels.customerService')}</ContactLabel>
                <ContactValue href="tel:+212661859009">+212-661859009</ContactValue>
              </ContactCard>
              <ContactCard>
                <ContactLabel>{t('pages.contact.directLabels.dispatch')}</ContactLabel>
                <ContactValue href="tel:+212700765376">+212-700765376</ContactValue>
              </ContactCard>
              <ContactCard>
                <ContactLabel>{t('pages.contact.directLabels.email')}</ContactLabel>
                <ContactValue href="mailto:pridetrans3@gmail.com">pridetrans3@gmail.com</ContactValue>
              </ContactCard>
            </ContactGrid>
          </ContactPanel>
        </HeroInner>
      </HeroSection>

      <Section>
        <SectionInner>
          <SectionTitle>{t('pages.contact.locationsTitle')}</SectionTitle>
          <Paragraph>{t('pages.contact.locationsIntro')}</Paragraph>
          <OfficeGrid>
            {locations.map((location) => (
              <OfficeCard key={location.title}>
                <OfficeTitle>{location.title}</OfficeTitle>
                <OfficeText>{location.text}</OfficeText>
              </OfficeCard>
            ))}
          </OfficeGrid>
        </SectionInner>
      </Section>

      <Section $alt>
        <SectionInner>
          <SectionTitle>{t('pages.contact.formTitle')}</SectionTitle>
          <Paragraph>{t('pages.contact.formIntro')}</Paragraph>
          <FormCard>
            <Form>
              <Row>
                <Label>
                  {t('pages.contact.form.name')}
                  <Input type="text" name="name" placeholder={t('pages.contact.placeholders.name')} required />
                </Label>
                <Label>
                  {t('pages.contact.form.company')}
                  <Input type="text" name="company" placeholder={t('pages.contact.placeholders.company')} />
                </Label>
              </Row>
              <Row>
                <Label>
                  {t('pages.contact.form.email')}
                  <Input type="email" name="email" placeholder={t('pages.contact.placeholders.email')} required />
                </Label>
                <Label>
                  {t('pages.contact.form.phone')}
                  <Input type="tel" name="phone" placeholder={t('pages.contact.placeholders.phone')} />
                </Label>
              </Row>
              <Label>
                {t('pages.contact.form.message')}
                <Textarea name="message" placeholder={t('pages.contact.placeholders.message')} required />
              </Label>
              <SubmitButton type="submit">{t('pages.contact.submit')}</SubmitButton>
            </Form>
            <Disclaimer>{t('pages.contact.disclaimer')}</Disclaimer>
          </FormCard>
        </SectionInner>
      </Section>

      <CTASection />
      <Footer />
    </>
  );
};

export default ContactPage;
